/**
 * The figure check (docs/analytics-assistant.md): numbers in an answer that aren't in the brief the
 * model was given. €, commas, K/M/B and % are normalised, and a figure matches when a brief value
 * rounds to it at the precision it was written with, so "€0.5M" matches €457K and "5%" matches 4.9%.
 */

export interface Figure {
  /** As written in the text, e.g. `€13.1M`. */
  text: string;
  /** Absolute value in base units: `€13.1M` → 13,100,000; `4.9%` → 4.9. */
  value: number;
  /** The multiplier the text was written in (1, 1e3, 1e6, 1e9). */
  scale: number;
  /** Decimals as written. */
  decimals: number;
}

const SCALES: Record<string, number> = {
  k: 1e3,
  thousand: 1e3,
  m: 1e6,
  million: 1e6,
  b: 1e9,
  bn: 1e9,
  billion: 1e9,
};

// Not inside a word, an id or a date (`qwen3`, `game:164`, `2026-09-01`, `3rd`).
const FIGURE =
  /(?<![\p{L}\p{N}_.,:/-])([-−+]?)([€$£]?)\s?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?(?:\s?(thousand|million|billion|bn|[kKmMbB])(?![\p{L}]))?(?:\s?(%|pp(?![\p{L}])))?(?![\p{L}\p{N}_])/gu;

/** Text without what isn't a figure: link targets, list markers, clock times. */
function prose(text: string): string {
  return text
    .replace(/\[([^\]\n]*)\]\((?:[^()\n]|\([^()\n]*\))*\)/g, '$1')
    .replace(/^\s*\d+[.)]\s/gm, ' ')
    .replace(/\b\d{1,2}:\d{2}\b/g, ' ');
}

export function extractFigures(text: string): Figure[] {
  const out: Figure[] = [];
  for (const match of prose(text).matchAll(FIGURE)) {
    const [whole, , , digits = '', fraction = '', unit] = match;
    const scale = unit ? (SCALES[unit.toLowerCase()] ?? 1) : 1;
    const mantissa = Number(`${digits.replace(/,/g, '')}${fraction ? `.${fraction}` : ''}`);
    if (!Number.isFinite(mantissa)) continue;
    out.push({ text: whole.trim(), value: mantissa * scale, scale, decimals: fraction.length });
  }
  return out;
}

const roundTo = (value: number, decimals: number) => {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
};

/** `figure` as written matches `value` rounded to the same unit and precision. */
function matches(figure: Figure, value: number): boolean {
  const written = roundTo(figure.value / figure.scale, figure.decimals);
  return Math.abs(roundTo(value / figure.scale, figure.decimals) - written) < 1e-9;
}

/** Figures in `answer` that none of `sources` contains, as written, without repeats. */
export function unmatchedFigures(answer: string, sources: string[]): string[] {
  const known = sources.flatMap(extractFigures).map((f) => f.value);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const figure of extractFigures(answer)) {
    if (known.some((value) => matches(figure, value))) continue;
    if (seen.has(figure.text)) continue;
    seen.add(figure.text);
    out.push(figure.text);
  }
  return out;
}
